import { PlayerListItemFragment, PlayerTransferFragment } from '@amf/shared/types/graphql';
import { fromUnixTime } from 'date-fns';
import Link from 'next/link';
import { useTranslate } from '@tolgee/react';
import { useSWRInfinite } from 'swr';
import { getCacheKey, LIMIT } from '@amf/shared/data/Player';
import Button from '@amf/shared/components/button/Button';
import Empty from '@amf/shared/components/utils/Empty';
import FormattedDate from '@amf/shared/components/utils/FormattedDate';

interface PlayerTransfersTableProps {
  transfers: PlayerTransferFragment[];
  teamPrefix: string;
}

function PlayerTransfersTable({ transfers, teamPrefix }: PlayerTransfersTableProps) {
  const { t } = useTranslate();
  return (
    <table className='PlayerTransfers__table'>
      <thead>
        <tr>
          <th className='PlayerTransfers__cell PlayerTransfers__cell--header'>
            {t('player.transfers.team')}
          </th>
          <th className='PlayerTransfers__cell PlayerTransfers__cell--header'>
            {t('player.transfers.type')}
          </th>
          <th className='PlayerTransfers__cell PlayerTransfers__cell--header'>
            {t('player.transfers.date')}
          </th>
        </tr>
      </thead>
      <tbody>
        {transfers.map(transfer => {
          const dateStart = fromUnixTime(transfer.dateStart);
          const dateEnd = transfer.dateEnd === null ? null : fromUnixTime(transfer.dateEnd);
          return (
            <tr key={transfer.id}>
              <td className='PlayerTransfers__cell PlayerTransfers__cell--title'>
                <strong>
                  <Link href={`${teamPrefix}/${transfer.team.id}`}>{transfer.team.name}</Link>
                </strong>
                <small>{transfer.team.club.name}</small>
              </td>
              <td className='PlayerTransfers__cell PlayerTransfers__cell--title'>
                <span>
                  {transfer.registration
                    ? t('player.transfers.type.registration')
                    : t(`player.transfers.type.${transfer.type}`)}
                </span>
              </td>
              <td className='PlayerTransfers__cell'>
                <span>
                  <FormattedDate date={dateStart} format='dd.MM.yyy' />
                  {dateEnd && (
                    <>
                      {' '}
                      &ndash; <FormattedDate date={dateEnd} format='dd.MM.yyy' />
                    </>
                  )}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface PlayerTransfersProps {
  player: PlayerListItemFragment;
  transfers: PlayerTransferFragment[];
  teamPrefix: string;
  title?: string;
}

function PlayerTransfers({
  player,
  transfers: initialData,
  teamPrefix,
  title,
}: PlayerTransfersProps) {
  const { t } = useTranslate();
  const { data, size, setSize } = useSWRInfinite<PlayerTransferFragment[]>(getCacheKey(player.id), {
    initialData: [initialData],
  });

  const transfers: PlayerTransferFragment[] = data
    ? ([] as PlayerTransferFragment[]).concat(...data)
    : [];

  const renderLoadMore = () => {
    if (!transfers || data === undefined) {
      return null;
    }

    if (size > data.length) {
      return (
        <Button type='button' variant='dark-outline' disabled>
          {t('player.transfers.loading')}
        </Button>
      );
    }

    if (LIMIT * size > transfers.length) {
      return null;
    }

    return (
      <Button type='button' variant='dark-outline' onClick={() => setSize(size + 1)}>
        {t('player.transfers.more')}
      </Button>
    );
  };

  return (
    <div className='PlayerTransfers'>
      {title && <h2>{title}</h2>}
      {transfers.length === 0 && <Empty>{t('player.transfers.empty')}</Empty>}
      {transfers.length > 0 && (
        <div className='PlayerTransfers__content'>
          <PlayerTransfersTable transfers={transfers} teamPrefix={teamPrefix} />
        </div>
      )}
      <div className='PlayerTransfers__pagination'>{renderLoadMore()}</div>
    </div>
  );
}

export default PlayerTransfers;
